import { isFilePreview } from '@/app/helpers/isFilePreview';

function getEmbedSettings() {
  // Route-based detection (new /embed route)
  const isEmbedRoute = window.location.pathname.startsWith('/embed');

  // Query-param based detection (legacy, for backward compatibility)
  const searchParams = new URLSearchParams(window.location.search);
  const isEmbedParam = searchParams.get('embed') !== null;

  const isEmbed = isEmbedRoute || isEmbedParam;

  // For embed route, AI is always disabled
  // For embed param, it can be explicitly disabled via noAi param
  const isAiDisabled = isEmbedRoute || searchParams.get('noAi') !== null;

  // noMultiplayer is always true when embedded (route or param)
  // Can also be explicitly set via query param
  const isNoMultiplayer = searchParams.get('noMultiplayer') !== null || isEmbed;

  // Sheet names to show (only in embed mode). Supports comma-separated list.
  // When set, only these sheets are visible. If only one sheet, the sheet bar is hidden.
  // For backwards compatibility, also supports the legacy `sheet` parameter (single sheet).
  const sheetsParam = isEmbed ? searchParams.get('sheets') : null;
  const sheetParam = isEmbed ? searchParams.get('sheet') : null;
  const embedSheetsFilter: string[] | null = isEmbed
    ? sheetsParam
      ? sheetsParam
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : sheetParam
        ? [sheetParam.trim()]
        : null
    : null;

  // Legacy: single sheet name (for backwards compatibility, prefer embedSheetsFilter)
  const embedSheetName = embedSheetsFilter?.length === 1 ? embedSheetsFilter[0] : null;

  // Readonly mode (only in embed mode). When set, the user cannot edit the sheet.
  const isReadonly = isEmbed && searchParams.get('readonly') !== null;

  // Hide headings (only in embed mode). When set, the row/column headings are hidden.
  const hideHeadings = isEmbed && searchParams.get('hideHeadings') !== null;

  // Hide grid lines (only in embed mode). When set, the grid lines are hidden.
  const hideGridLines = isEmbed && searchParams.get('hideGridLines') !== null;

  // No formatting (only in embed mode). When set, the formatting bar and goto menu are hidden.
  const noFormatting = isEmbed && searchParams.get('noFormatting') !== null;

  // Clamp to a specific range or table (only in embed mode). When set, viewport is clamped to the range bounds.
  // Accepts either a table name (e.g., "TABLE_NAME") or an A1 range (e.g., "A1:D10").
  const clampToRange = isEmbed ? searchParams.get('clamp') : null;

  // Clamp to content (only in embed mode). ON by default - prevents panning beyond sheet content.
  // When `noClampToContent` param is present, clamping is disabled.
  // Also enabled when clampToRange is set (can't clamp to range without clamping behavior).
  const clampToContent = isEmbed && (searchParams.get('noClampToContent') === null || clampToRange !== null);

  // Disable zoom (only in embed mode). When set, zooming is locked and zoom UI is hidden.
  const disableZoom = isEmbed && searchParams.get('noZoom') !== null;

  // Initial viewport fit to a range or table (only in embed mode). Does not clamp or filter sheets.
  const initialViewRange = isEmbed ? searchParams.get('initialView') : null;

  return {
    isEmbed,
    isAiDisabled,
    isNoMultiplayer,
    embedSheetsFilter,
    embedSheetName,
    isReadonly,
    hideHeadings,
    hideGridLines,
    noFormatting,
    clampToContent,
    clampToRange,
    disableZoom,
    initialViewRange,
  };
}

const embedSettings = getEmbedSettings();

/**
 * This can be used in various places to determine whether the app
 * is currently in "embed" mode. Detected via:
 * - Route-based: /embed route (new)
 * - Query-param: ?embed on /file/:uuid (legacy, for backward compatibility)
 */
export const isEmbed = embedSettings.isEmbed;

/**
 * Whether AI features should be disabled.
 * - Always true for /embed route
 * - Controlled by `noAi` query param for legacy ?embed mode
 * - Always true inside the dashboard Agent Chat's read-only preview pane
 */
export const isAiDisabled = (): boolean => embedSettings.isAiDisabled || isFilePreview();

/**
 * Whether multiplayer and offline transaction saving should be disabled.
 * When true, changes are local only and lost when refreshed.
 * Always true when embedded (via route or query param).
 */
export const isNoMultiplayer = embedSettings.isNoMultiplayer;

/**
 * List of sheet names to show in embed mode.
 * When set via `sheets` (comma-separated) or `sheet` query param in embed mode,
 * only matching sheets are visible. If only one sheet matches, the sheet bar is hidden.
 */
export const embedSheetsFilter = embedSettings.embedSheetsFilter;

/**
 * The sheet name to show in embed mode (legacy, for single-sheet embeds).
 * When only one sheet is in the filter, this contains that name.
 * Used for backwards compatibility with code expecting a single sheet name.
 */
export const embedSheetName = embedSettings.embedSheetName;

/**
 * Whether the embed is in readonly mode.
 * When set via `readonly` query param in embed mode, the user cannot
 * edit the sheet.
 */
export const isReadonly = embedSettings.isReadonly;

/**
 * Whether row/column headings should be hidden in embed mode.
 * When set via `hideHeadings` query param in embed mode, the headings are hidden.
 */
export const embedHideHeadings = embedSettings.hideHeadings;

/**
 * Whether grid lines should be hidden in embed mode.
 * When set via `hideGridLines` query param in embed mode, the grid lines are hidden.
 */
export const embedHideGridLines = embedSettings.hideGridLines;

/**
 * Whether the formatting bar and goto menu should be hidden in embed mode.
 * When set via `noFormatting` query param in embed mode, these UI elements are hidden.
 */
export const embedNoFormatting = embedSettings.noFormatting;

/**
 * Whether the viewport should be clamped to content bounds in embed mode.
 * ON by default for embeds - prevents panning beyond sheet content.
 * Disabled when `noClampToContent` query param is present.
 */
export const embedClampToContent = embedSettings.clampToContent;

/**
 * Range or table name to clamp viewport to in embed mode.
 * When set via `clamp` query param in embed mode, viewport is restricted
 * to the specified range bounds instead of full sheet content.
 * Accepts a table name (e.g., "TABLE_NAME") or an A1 range (e.g., "A1:D10").
 */
export const embedClampToRange = embedSettings.clampToRange;

/**
 * Whether zooming should be disabled in embed mode.
 * When set via `noZoom` query param in embed mode, zooming is locked and
 * zoom UI (menu, pinch, wheel) is disabled.
 */
export const embedDisableZoom = embedSettings.disableZoom;

/**
 * Range or table name to fit the viewport to on embed load in embed mode.
 * When set via `initialView` query param, the viewport scales and scrolls once
 * so that region is visible. Does not restrict panning or sheet visibility.
 * Accepts a table name (e.g., "TABLE_NAME") or an A1 range (e.g., "A1:D10").
 */
export const embedInitialViewRange = embedSettings.initialViewRange;
